import React, { useEffect, useRef, useState } from 'react';
import { LOGO_URL } from './Logo';

const SPLASH_TTL_MS = 30 * 1000;

const shouldShowSplash = () => {
  try {
    const last = Number(sessionStorage.getItem('feron_splash_at') || 0);
    return Date.now() - last > SPLASH_TTL_MS;
  } catch {
    return true;
  }
};

// Generate a neon 'whoosh' with the Web Audio API (no asset needed).
const playWhoosh = () => {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return false;
    const ctx = new AC();

    const finalize = () => {
      const now = ctx.currentTime;

      // Sweep oscillator
      const osc = ctx.createOscillator();
      const oscGain = ctx.createGain();
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(120, now);
      osc.frequency.exponentialRampToValueAtTime(900, now + 0.55);
      oscGain.gain.setValueAtTime(0.0001, now);
      oscGain.gain.exponentialRampToValueAtTime(0.09, now + 0.15);
      oscGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.9);

      // Filter for a swoosh feel
      const filter = ctx.createBiquadFilter();
      filter.type = 'bandpass';
      filter.frequency.setValueAtTime(300, now);
      filter.frequency.exponentialRampToValueAtTime(3000, now + 0.6);
      filter.Q.value = 6;

      // Noise for texture
      const bufferSize = ctx.sampleRate * 0.9;
      const noiseBuffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
      const data = noiseBuffer.getChannelData(0);
      for (let i = 0; i < bufferSize; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / bufferSize);
      const noise = ctx.createBufferSource();
      noise.buffer = noiseBuffer;
      const noiseGain = ctx.createGain();
      noiseGain.gain.setValueAtTime(0.0001, now);
      noiseGain.gain.exponentialRampToValueAtTime(0.05, now + 0.1);
      noiseGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.9);

      osc.connect(oscGain).connect(filter).connect(ctx.destination);
      noise.connect(noiseGain).connect(ctx.destination);

      osc.start(now);
      osc.stop(now + 1);
      noise.start(now);
      noise.stop(now + 1);

      setTimeout(() => { try { ctx.close(); } catch {} }, 1500);
    };

    // Browsers block AudioContext until a gesture. Resume first, then play.
    if (ctx.state === 'suspended') {
      ctx.resume().then(finalize).catch(() => finalize());
    } else {
      finalize();
    }
    return true;
  } catch (err) {
    console.warn('[Splash] audio failed:', err.message);
    return false;
  }
};

const WolfSplash = () => {
  const [visible, setVisible] = useState(false);
  const [phase, setPhase] = useState('in');
  const timersRef = useRef([]);
  const overflowRef = useRef('');

  const dismiss = () => {
    setPhase('out');
    setTimeout(() => {
      setVisible(false);
      document.body.style.overflow = overflowRef.current;
    }, 500);
  };

  useEffect(() => {
    if (!shouldShowSplash()) return;
    try { sessionStorage.setItem('feron_splash_at', String(Date.now())); } catch {}

    setVisible(true);
    overflowRef.current = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Try playing whoosh immediately. If browser blocks autoplay, retry on first gesture.
    let played = playWhoosh();
    const retry = () => {
      if (played) return;
      played = playWhoosh();
      if (played) {
        window.removeEventListener('pointerdown', retry);
        window.removeEventListener('keydown', retry);
        window.removeEventListener('touchstart', retry);
      }
    };
    if (!played) {
      window.addEventListener('pointerdown', retry, { once: false });
      window.addEventListener('keydown', retry, { once: false });
      window.addEventListener('touchstart', retry, { once: false });
    }

    const t1 = setTimeout(() => setPhase('hold'), 500);
    const t2 = setTimeout(() => setPhase('out'), 1200);
    const t3 = setTimeout(() => {
      setVisible(false);
      document.body.style.overflow = overflowRef.current;
    }, 1700);
    timersRef.current = [t1, t2, t3];

    return () => {
      timersRef.current.forEach(clearTimeout);
      document.body.style.overflow = overflowRef.current;
      window.removeEventListener('pointerdown', retry);
      window.removeEventListener('keydown', retry);
      window.removeEventListener('touchstart', retry);
    };
  }, []);

  if (!visible) return null;

  return (
    <div
      className={`fixed inset-0 z-[9999] flex items-center justify-center bg-black transition-opacity duration-500 ${phase === 'out' ? 'opacity-0' : 'opacity-100'}`}
      aria-hidden="true"
    >
      <div className="absolute inset-0 pointer-events-none animate-[pulseGlow_2.5s_ease-in-out_infinite]"
        style={{ background: 'radial-gradient(circle at 50% 50%, rgba(57,255,20,0.28) 0%, rgba(57,255,20,0.06) 40%, transparent 72%)' }} />
      <div className="absolute inset-0 opacity-20 pointer-events-none"
        style={{
          backgroundImage: 'linear-gradient(rgba(57,255,20,0.25) 1px, transparent 1px), linear-gradient(90deg, rgba(57,255,20,0.25) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
          maskImage: 'radial-gradient(circle at 50% 50%, black 30%, transparent 75%)'
        }} />

      <div className="relative flex flex-col items-center">
        <div className="relative animate-[wolfIn_1.6s_cubic-bezier(0.16,1,0.3,1)_forwards]">
          <img src={LOGO_URL} alt="Feron" draggable="false"
            className="h-60 md:h-80 w-auto object-contain drop-shadow-[0_0_40px_rgba(57,255,20,0.95)]" />
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <div className="absolute left-0 right-0 h-[3px] bg-[#39ff14] animate-[scanLine_2.8s_ease-in-out]"
              style={{ boxShadow: '0 0 12px 3px rgba(57,255,20,0.9)' }} />
          </div>
        </div>

        <div className="mt-6 flex items-center gap-3 animate-[fadeUp_1.6s_ease-out_0.9s_both]">
          <span className="h-[2px] w-10 bg-[#39ff14] animate-[pulseBar_1s_ease-in-out_infinite]" />
          <span className="text-[#39ff14] text-xs md:text-sm font-black tracking-[0.4em] uppercase">Welcome to the Pack</span>
          <span className="h-[2px] w-10 bg-[#39ff14] animate-[pulseBar_1s_ease-in-out_infinite]" />
        </div>
      </div>

      {/* Skip intro */}
      <button
        onClick={dismiss}
        className="absolute top-6 right-6 z-10 text-[10px] md:text-xs font-bold tracking-[0.3em] text-[#39ff14]/80 hover:text-[#39ff14] border border-[#39ff14]/40 hover:border-[#39ff14] px-4 py-2 rounded-full transition backdrop-blur-sm animate-[fadeUp_0.8s_ease-out_0.4s_both]"
      >
        SKIP INTRO →
      </button>

      <style>{`
        @keyframes wolfIn {
          0%   { transform: scale(0.55) rotate(-3deg); opacity: 0; filter: blur(10px) brightness(1.4); }
          55%  { transform: scale(1.10) rotate(1deg); opacity: 1; filter: blur(0) brightness(1.2); }
          100% { transform: scale(1) rotate(0deg);   opacity: 1; filter: blur(0) brightness(1); }
        }
        @keyframes scanLine {
          0%   { top: 0%;   opacity: 0; }
          15%  { opacity: 1; }
          85%  { opacity: 1; }
          100% { top: 100%; opacity: 0; }
        }
        @keyframes pulseBar {
          0%, 100% { opacity: 0.4; transform: scaleX(0.85); }
          50%      { opacity: 1;   transform: scaleX(1); }
        }
        @keyframes pulseGlow {
          0%, 100% { opacity: 0.85; }
          50%      { opacity: 1; }
        }
        @keyframes fadeUp {
          0%   { opacity: 0; transform: translateY(12px); }
          100% { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default WolfSplash;
